import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { SiteHeader, SiteFooter } from "@/components/SiteChrome";
import { EnquiryForm } from "@/components/EnquiryForm";
import { ServiceHeroVisual } from "@/components/ServiceHeroVisual";
import { services, serviceBySlug } from "@/data/services";
import { TextileRecyclingContent } from "@/components/TextileRecyclingContent";
import { SecurityBlock, RecoveryTiersBlock } from "@/components/SecurityBlocks";
import { textileRecycling } from "@/data/textile-recycling";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { RelatedArticles, RelatedServices } from "@/components/RelatedLinks";
import { serviceSeo } from "@/data/service-faqs";
import { breadcrumbScript, defaultOgImageMeta, ORG_ID } from "@/lib/seo";

export const Route = createFileRoute("/services/$slug")({
  loader: ({ params }) => {
    const service = serviceBySlug[params.slug];
    if (!service) throw notFound();
    return { service };
  },
  head: ({ loaderData, params }) => {
    const s = loaderData?.service;
    if (!s) return { meta: [{ title: "Service | Preserve Earth" }] };
    if (params.slug === "textile-recycling") {
      const { seoTitle, description, url, image, faqs } = textileRecycling;
      return {
        meta: [
          { title: seoTitle },
          { name: "description", content: description },
          { property: "og:title", content: seoTitle },
          { property: "og:description", content: description },
          { property: "og:url", content: url },
          { property: "og:type", content: "website" },
          { property: "og:site_name", content: "Preserve Earth" },
          { property: "og:image", content: image },
          { name: "twitter:card", content: "summary_large_image" },
          { name: "twitter:title", content: seoTitle },
          { name: "twitter:description", content: description },
          { name: "twitter:image", content: image },
        ],
        links: [{ rel: "canonical", href: url }],
        scripts: [
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Service",
              "@id": `${url}#service`,
              serviceType: "Textile recycling",
              name: textileRecycling.heading,
              description,
              url,
              areaServed: [
                { "@type": "Country", name: "United Kingdom" },
                { "@type": "Country", name: "France" },
                { "@type": "Country", name: "Belgium" },
              ],
              provider: { "@id": ORG_ID },
            }),
          },
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: faqs.map((faq) => ({ "@type": "Question", name: faq.question, acceptedAnswer: { "@type": "Answer", text: faq.answer } })),
            }),
          },
          breadcrumbScript([{ name: "Services", path: "/services" }, { name: s.title, path: `/services/${params.slug}` }]),
        ],
      };
    }
    const url = `https://preserveearth.co.uk/services/${params.slug}`;
    const seo = serviceSeo[params.slug];
    const title = seo?.seoTitle || `${s.title} | Preserve Earth`;
    const desc = seo?.seoDescription || s.metaDescription;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        ...defaultOgImageMeta,
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "@id": `${url}#service`,
            name: s.title,
            description: desc,
            serviceType: s.title,
            url,
            areaServed: [
              { "@type": "Country", name: "United Kingdom" },
              { "@type": "Country", name: "France" },
              { "@type": "Country", name: "Belgium" },
            ],
            provider: { "@id": ORG_ID },
          }),
        },
        ...(seo?.faqs?.length
          ? [{
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                mainEntity: seo.faqs.map((f) => ({ "@type": "Question", name: f.question, acceptedAnswer: { "@type": "Answer", text: f.answer } })),
              }),
            }]
          : []),
        breadcrumbScript([{ name: "Services", path: "/services" }, { name: s.title, path: `/services/${params.slug}` }]),
      ],
    };
  },
  notFoundComponent: () => (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <section className="container-page py-32 text-center">
        <p className="mb-4 text-xs font-medium uppercase tracking-[0.25em] text-moss">
          Not found
        </p>
        <h1 className="font-serif text-4xl text-charcoal">Service not found</h1>
        <p className="mt-4 text-charcoal/70">
          The service you're looking for doesn't exist.
        </p>
        <Link
          to="/services"
          className="mt-8 inline-block rounded-sm bg-moss px-6 py-3 text-sm text-cream"
        >
          View all services
        </Link>
      </section>
      <SiteFooter />
    </div>
  ),
  errorComponent: ({ reset }) => (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <section className="container-page py-32 text-center">
        <h1 className="font-serif text-3xl text-charcoal">Something went wrong</h1>
        <button
          onClick={reset}
          className="mt-6 rounded-sm bg-moss px-6 py-3 text-sm text-cream"
        >
          Try again
        </button>
      </section>
      <SiteFooter />
    </div>
  ),
  component: ServicePage,
});

function ServicePage() {
  const { service } = Route.useLoaderData() as { service: import("@/data/services").Service };


  const others = services.filter((s) => s.slug !== service.slug);
  const seo = serviceSeo[service.slug];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader enquireHref="#enquire" />

      <main>
        {/* Hero */}
        <section className={`relative overflow-hidden bg-moss text-cream ${service.slug === "textile-recycling" ? "textile-mobile-hero" : "svc-mobile-hero"}`}>
          {/* Full-bleed animated visual */}
          <div className="absolute inset-0">
            <ServiceHeroVisual slug={service.slug} fullBleed />
          </div>
          {/* Readability overlays */}
          <div className={`textile-readability-overlay pointer-events-none absolute inset-0 bg-gradient-to-r from-moss/95 via-moss/70 to-moss/10`} />
          <div className="textile-readability-overlay pointer-events-none absolute inset-0 bg-gradient-to-t from-moss/80 via-transparent to-moss/30" />

          <div className="container-page relative grid gap-12 py-24 md:py-32 lg:grid-cols-[1.1fr_1fr] lg:gap-16">
            <div className={`relative ${service.slug === "textile-recycling" ? "textile-mobile-copy" : ""}`}>
              <Breadcrumbs tone="dark" crumbs={[{ name: "Services", path: "/services" }, { name: service.title, path: `/services/${service.slug}` }]} />
              <p className="mb-5 font-mono text-xs tracking-[0.25em] text-cream/70">
                {service.n} / 05 · Services
              </p>
              <h1 className="font-serif text-4xl leading-tight md:text-5xl lg:text-6xl">
                {service.slug === "textile-recycling" ? textileRecycling.heading : service.title}
              </h1>
              <p className="mt-6 max-w-xl text-pretty text-lg leading-relaxed text-cream/85">
                {service.tagline}
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-4">
                <a
                  href="#enquire"
                  className="rounded-sm bg-cream px-6 py-3 text-sm font-medium uppercase tracking-wide text-charcoal transition-colors hover:bg-cream/90"
                >
                  Get a tailored quote
                </a>
                <p className="text-sm text-cream/85">
                  Or email{" "}
                  <a href="mailto:info@preserveearth.co.uk" className="underline underline-offset-4 hover:text-cream">
                    info@preserveearth.co.uk
                  </a>
                </p>
              </div>

              {service.slug !== "textile-recycling" && <ul className="mt-10 grid gap-2 sm:grid-cols-2">
                {service.highlights.map((h) => (
                  <li
                    key={h}
                    className="flex items-start gap-2 text-sm text-cream/85"
                  >
                    <span aria-hidden className="mt-[2px] text-cream/55">
                      ✓
                    </span>
                    <span>{h}</span>
                  </li>
                ))}
              </ul>}
            </div>
          </div>
        </section>

        {/* Sections */}
        {service.slug === "textile-recycling" ? <TextileRecyclingContent /> : <section className="container-page py-24 md:py-32">
          {seo?.answerFirst && (
            <div className="mx-auto mb-16 max-w-5xl border-l-2 border-moss pl-6">
              <h2 className="font-serif text-2xl text-charcoal md:text-3xl">{seo.answerFirst.question}</h2>
              <p className="mt-4 max-w-[70ch] text-pretty text-lg leading-relaxed text-charcoal/80">{seo.answerFirst.answer}</p>
            </div>
          )}
          <div className="mx-auto grid max-w-5xl gap-16">
            {service.sections.map((sec) => (
              <article key={sec.heading} className="grid gap-4 md:grid-cols-[1fr_2fr] md:gap-12">
                <h2 className="font-serif text-2xl text-charcoal md:text-3xl">
                  {sec.heading}
                </h2>
                <div className="text-base leading-relaxed text-charcoal/75">
                  {sec.body && <p className="text-pretty">{sec.body}</p>}
                  {sec.bullets && (
                    <ul className="mt-2 list-disc space-y-2 pl-5 marker:text-moss">
                      {sec.bullets.map((b) => (
                        <li key={b}>{b}</li>
                      ))}
                    </ul>
                  )}
                  {sec.link && (
                    <p className="mt-4">
                      <a href={sec.link.href} className="text-moss underline decoration-moss/40 underline-offset-4 hover:decoration-moss">
                        {sec.link.label}
                      </a>
                    </p>
                  )}
                </div>
              </article>
            ))}
          </div>
          {seo?.faqs?.length ? (
            <div className="mx-auto mt-24 max-w-5xl">
              <h2 className="font-serif text-3xl text-charcoal md:text-4xl">Frequently asked questions</h2>
              <div className="mt-8 divide-y divide-charcoal/10 border-y border-charcoal/10">
                {seo.faqs.map((f) => (
                  <details key={f.question} className="group pe-faq py-5">
                    <summary className="flex cursor-pointer list-none items-start justify-between gap-6 font-serif text-xl text-charcoal">
                      <h3>{f.question}</h3>
                      <span aria-hidden className="mt-1 text-moss transition-transform duration-accordion group-open:rotate-45">+</span>
                    </summary>
                    <p className="mt-3 max-w-[70ch] text-pretty leading-relaxed text-charcoal/75">{f.answer}</p>
                  </details>
                ))}
              </div>
            </div>
          ) : null}
        </section>}

        <SecurityBlock />
        {service.slug === "textile-recycling" && <RecoveryTiersBlock />}

        {/* Enquiry */}
        <section id="enquire" className="container-page py-24 md:py-32">
          <div className="grid gap-16 lg:grid-cols-2 lg:gap-24">
            <div>
              <p className="mb-4 text-xs font-medium uppercase tracking-[0.25em] text-moss">
                Enquire
              </p>
              <h2 className="font-serif text-3xl text-charcoal md:text-4xl">
                Request a quote for {service.title.toLowerCase()}.
              </h2>
              <p className="mt-6 max-w-md text-pretty leading-relaxed text-charcoal/70">
                Send us the details and our team will respond with a tailored
                proposal. All enquiries go directly to{" "}
                <a className="underline decoration-moss/40 underline-offset-4 hover:decoration-moss" href="mailto:info@preserveearth.co.uk">
                  info@preserveearth.co.uk
                </a>
                .
              </p>
              <dl className="mt-10 space-y-6 text-sm">
                <div>
                  <dt className="text-[10px] font-semibold uppercase tracking-[0.2em] text-charcoal/70">
                    Email
                  </dt>
                  <dd className="mt-1">
                    <a className="hover:text-moss" href="mailto:info@preserveearth.co.uk">
                      info@preserveearth.co.uk
                    </a>
                  </dd>
                </div>
                <div>
                  <dt className="text-[10px] font-semibold uppercase tracking-[0.2em] text-charcoal/70">
                    Phone
                  </dt>
                  <dd className="mt-1">
                    <a className="hover:text-moss" href="tel:+443300432265">
                      0330 043 2265
                    </a>
                  </dd>
                </div>
                <div>
                  <dt className="text-[10px] font-semibold uppercase tracking-[0.2em] text-charcoal/70">
                    Coverage
                  </dt>
                  <dd className="mt-1 text-charcoal/70">Collections across the UK. We also serve clients in France and Belgium, who courier their items securely to our depot in France.</dd>
                </div>
              </dl>
            </div>

            <EnquiryForm defaultInterest={service.title} />
          </div>
        </section>

        {/* Related services + articles */}
        <RelatedServices slugs={others.map((o) => o.slug)} className="bg-cream" />
        <RelatedArticles slugs={seo?.relatedArticles ?? []} className="bg-cream" />
      </main>

      <SiteFooter />
    </div>
  );
}
